import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
  Switch,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import {
  adminPaymentsApi,
  type PaymentSettings,
} from "@/services/api";
import {
  ArrowLeft,
  Loader2,
  DollarSign,
  Info,
  Settings,
  Save,
} from "lucide-react";
import { toast } from "sonner";

export function AdminPaymentSettingsPage() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<PaymentSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [feeEnabled, setFeeEnabled] = useState(false);
  const [feeType, setFeeType] = useState<"FIXED" | "PERCENTAGE">("FIXED");
  const [feeFixedAmountUsd, setFeeFixedAmountUsd] = useState("");
  const [feePercentage, setFeePercentage] = useState("");
  const [minFeeUsd, setMinFeeUsd] = useState("");
  const [maxFeeUsd, setMaxFeeUsd] = useState("");

  useEffect(() => {
    loadSettings();
  }, []);

  async function loadSettings() {
    try {
      setLoading(true);
      const data = await adminPaymentsApi.getSettings();
      setSettings(data);
      setFeeEnabled(data.feeEnabled);
      setFeeType(data.feeType);
      setFeeFixedAmountUsd(data.feeFixedAmountUsd?.toString() || "0");
      setFeePercentage(data.feePercentage ? (data.feePercentage * 100).toString() : "0");
      setMinFeeUsd(data.minFeeUsd?.toString() || "0");
      setMaxFeeUsd(data.maxFeeUsd?.toString() || "");
    } catch (error) {
      console.error("Erro ao carregar configurações:", error);
      toast.error("Erro ao carregar configuracoes", {
        style: { background: "#495565", color: "#ffffff" },
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      const updateData: Partial<PaymentSettings> = {
        feeEnabled,
        feeType,
        feeFixedAmountUsd: parseFloat(feeFixedAmountUsd) || 0,
        feePercentage: feeType === "PERCENTAGE" ? (parseFloat(feePercentage) || 0) / 100 : 0,
        minFeeUsd: parseFloat(minFeeUsd) || 0,
        maxFeeUsd: maxFeeUsd ? parseFloat(maxFeeUsd) : null,
      };

      await adminPaymentsApi.updateSettings(updateData);
      loadSettings();
      toast.success("Configuracoes salvas com sucesso", {
        style: { background: "#495565", color: "#ffffff" },
      });
    } catch (error) {
      console.error("Erro ao salvar configurações:", error);
      toast.error("Erro ao salvar configuracoes", {
        style: { background: "#495565", color: "#ffffff" },
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Settings className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Configurações de Pagamento"
        description="Gerencie taxas de serviço e custos operacionais"
        actions={
          <div className="flex items-center gap-2">
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={() => navigate("/admin/payments")}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
          </div>
        }
      />

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Service Fee Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Taxa de Serviço
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-medium">Cobrar taxa de serviço</Label>
                  <p className="text-sm text-muted-foreground">
                    Quando ativo, uma taxa será adicionada ao valor de cada pagamento
                  </p>
                </div>
                <Switch checked={feeEnabled} onCheckedChange={setFeeEnabled} />
              </div>

              {feeEnabled && (
                <>
                  {/* Fee Type Radio */}
                  <div className="space-y-3">
                    <Label className="font-medium">Tipo de cobrança</Label>
                    <div className="flex flex-col gap-3">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="feeType"
                          value="FIXED"
                          checked={feeType === "FIXED"}
                          onChange={() => setFeeType("FIXED")}
                          className="h-4 w-4 accent-primary"
                        />
                        <div>
                          <span className="font-medium">Valor Fixo (USD)</span>
                          <p className="text-sm text-muted-foreground">
                            Um valor fixo por transação, independente do montante
                          </p>
                        </div>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="feeType"
                          value="PERCENTAGE"
                          checked={feeType === "PERCENTAGE"}
                          onChange={() => setFeeType("PERCENTAGE")}
                          className="h-4 w-4 accent-primary"
                        />
                        <div>
                          <span className="font-medium">Percentual (%)</span>
                          <p className="text-sm text-muted-foreground">
                            Um percentual sobre o valor do pagamento
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Fixed Amount */}
                  {feeType === "FIXED" && (
                    <div className="space-y-2">
                      <Label>Valor por transação (USD)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={feeFixedAmountUsd}
                        onChange={(e) => setFeeFixedAmountUsd(e.target.value)}
                        placeholder="Ex: 25.00"
                      />
                      <p className="text-xs text-muted-foreground">
                        Valor fixo em dólares cobrado em cada pagamento
                      </p>
                    </div>
                  )}

                  {/* Percentage */}
                  {feeType === "PERCENTAGE" && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Percentual (%)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={feePercentage}
                          onChange={(e) => setFeePercentage(e.target.value)}
                          placeholder="Ex: 1.50"
                        />
                        <p className="text-xs text-muted-foreground">
                          Percentual sobre o valor do pagamento
                        </p>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Mínimo (USD)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={minFeeUsd}
                            onChange={(e) => setMinFeeUsd(e.target.value)}
                            placeholder="Ex: 5.00"
                          />
                          <p className="text-xs text-muted-foreground">
                            Fee mínimo cobrado por transação
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label>Máximo (USD) - opcional</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={maxFeeUsd}
                            onChange={(e) => setMaxFeeUsd(e.target.value)}
                            placeholder="Sem teto"
                          />
                          <p className="text-xs text-muted-foreground">
                            Fee máximo por transação (vazio = sem teto)
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Save Button */}
              <div className="pt-2">
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Salvar Configurações
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Provider Cost Card */}
          <Card className="border-blue-500/20 bg-blue-500/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-600">
                <Info className="h-5 w-5" />
                Custo do Provedor
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Taxa do provedor por transação</span>
                  <span className="font-semibold">
                    {settings?.providerFeePercentage != null
                      ? `${(settings.providerFeePercentage * 100).toFixed(2)}%`
                      : "Não configurado"}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Este é o custo cobrado pelo provedor de pagamento (ex: UnblockPay). Esse custo{" "}
                  <strong>não</strong> é repassado diretamente ao cliente - ele é absorvido ou
                  compensado pela taxa de serviço configurada acima.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/admin/settings/payment-gateway")}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Editar na config do provedor
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Last updated info */}
          {settings?.updatedAt && (
            <p className="text-xs text-muted-foreground text-right">
              Última atualização: {new Date(settings.updatedAt).toLocaleDateString("pt-BR")}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
